import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

export type PosInstallationState = 'PENDING' | 'ACTIVE' | 'REVOKED';

export interface PosInstallation {
  id: string;
  venueId: string;
  name: string;
  state: PosInstallationState;
  enrollmentCodeExpiresAt: string | null;
  enrolledAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PosInstallationList {
  items: PosInstallation[];
}

export interface CreatedPosInstallation {
  device: PosInstallation;
  enrollmentCode: string;
}

const listKey = (venueId: string) => ['venues', venueId, 'pos-devices'] as const;

export const getPosDevices = (venueId: string): Promise<PosInstallationList> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/pos-devices', {
      params: { path: { venueId } },
    }),
  ) as Promise<PosInstallationList>;

export const createPosDevice = (venueId: string, name: string): Promise<CreatedPosInstallation> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/pos-devices', {
      params: { path: { venueId } },
      body: { name },
    }),
  ) as Promise<CreatedPosInstallation>;

export const revokePosDevice = (venueId: string, deviceId: string): Promise<PosInstallation> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/pos-devices/{deviceId}/revoke', {
      params: { path: { venueId, deviceId } },
    }),
  ) as Promise<PosInstallation>;

export function usePosDevices(venueId: string, enabled = true) {
  return useQuery({
    queryKey: listKey(venueId),
    queryFn: () => getPosDevices(venueId),
    enabled: enabled && !!venueId,
  });
}

function useInvalidateDevices(venueId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: listKey(venueId) });
}

export function useCreatePosDevice(venueId: string) {
  const invalidate = useInvalidateDevices(venueId);
  return useMutation({
    mutationFn: (name: string) => createPosDevice(venueId, name),
    onSuccess: invalidate,
  });
}

export function useRevokePosDevice(venueId: string) {
  const invalidate = useInvalidateDevices(venueId);
  return useMutation({
    mutationFn: (deviceId: string) => revokePosDevice(venueId, deviceId),
    onSuccess: invalidate,
  });
}
